import { LyfeTooltip } from "@lyfegen-platform/react-common";
import { Box, Button, Divider, Popover, Typography } from "@material-ui/core";
import { Close, ExpandMore } from "@material-ui/icons";
import { withStyles } from "@material-ui/styles";
import { format } from "date-fns";
import React, { Dispatch, useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { blueContrast } from "../../hooks/use-map-theme-styles";
import renderCurrency from "../../utils/currency";
import getDatePattern from "../../utils/date-pattern";
import { onlyFirstLetterUppercase } from "../../utils/first-letter-uppercase";
import { HorizontalScrollContainer } from "./HorizontalScrollContainer";
import {
	ChipFilterState,
	FilterGroup,
	FilterListStateType,
	FilterSelectionType,
	FilterStateType,
	isFilterEmpty,
	ListFilterOption,
	ListFilterState,
	TableFilterAction,
} from "./models";
import { renderFilterComponent } from "./renderFilterComponent";
import TabsGroup from "./TabsGroup";

const dateFormat = getDatePattern();

function getFilterComponent(
	filter: FilterStateType,
	dispatcher: (action: TableFilterAction) => void,
	customOnSelectAction?: () => void,
	handlePopoverClose?: () => void
) {
	if (filter.selectionType === FilterSelectionType.CHIP) {
		return <></>;
	}

	return renderFilterComponent(filter, {
		usePopover: true,
		dispatcher,
		customOnSelectAction,
		handlePopoverClose,
	});
}

const PermanentFilterButton = withStyles((theme) => ({
	root: {
		borderRadius: "4px",
		borderWidth: "1px",
		borderStyle: "solid",
		borderColor: blueContrast,
		padding: 0,
		paddingLeft: theme.spacing(1),
		height: "auto",
		textTransform: "none",
		width: "auto",
	},
}))(Button);

export const ClearPermanentFilterButton = withStyles({
	root: {
		padding: "0 2px 0 2px",
		height: "auto",
		minWidth: 0,
	},
})(Button);

interface IProps {
	filter: FilterStateType;
	dispatcher: Dispatch<TableFilterAction>;
	customOnSelectAction?: () => void;
	children: React.ReactElement;
}

function renderOption(filter: ListFilterState, option: ListFilterOption, isEllipsisRuleApplied: boolean) {
	const isActiveContent = true;

	return filter.renderOption ? (
		filter.renderOption(option, isActiveContent)
	) : (
		<Typography
			color="primary"
			style={
				isEllipsisRuleApplied ? { maxWidth: 300, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" } : undefined
			}
		>
			{option.label}
		</Typography>
	);
}

const ActiveFilterContent = (props: { filter: FilterStateType; isEllipsisRuleApplied: boolean }) => {
	const { t } = useTranslation();

	if (props.filter.selectionType === FilterSelectionType.LIST) {
		const firstSelectedOption = props.filter.options.find((option) => option.isSelected)!;
		const firstSelectedNestedOption = firstSelectedOption?.nested?.options.find((nestedOption) => nestedOption.isSelected);

		if (!props.isEllipsisRuleApplied) {
			const nestedOptions = firstSelectedOption.nested?.options
				.filter((option) => option.isSelected)
				.map((nested) => nested.label)
				.join(", ");

			return <Typography>{`${firstSelectedOption.label} ${nestedOptions ? "• " + nestedOptions : ""}`}</Typography>;
		}

		return (
			<Box display="flex" alignItems="center">
				{firstSelectedNestedOption ? (
					<Box display="flex" alignItems="center">
						{renderOption(props.filter, firstSelectedOption, props.isEllipsisRuleApplied)}
						<span
							style={{
								display: "inline-block",
								verticalAlign: "middle",
								marginLeft: 8,
								marginRight: 8,
								width: 4,
								height: 4,
								backgroundColor: "#367bf5",
								borderRadius: "50%",
							}}
						/>
						{renderOption(firstSelectedOption.nested!, firstSelectedNestedOption, props.isEllipsisRuleApplied)}
					</Box>
				) : (
					renderOption(props.filter, firstSelectedOption, props.isEllipsisRuleApplied)
				)}
				{!!props.filter.selectedValueCount && props.filter.selectedValueCount > 1 && (
					<Typography color="primary" style={{ marginLeft: 5, marginRight: 5 }}>
						+{props.filter.selectedValueCount - 1}
					</Typography>
				)}
			</Box>
		);
	}

	if (props.filter.selectionType === FilterSelectionType.DATE_RANGE) {
		if (props.filter.rangeType === "custom") {
			return (
				<Typography color="primary" noWrap>
					{props.filter.startValue && format(props.filter.startValue, dateFormat)} -{" "}
					{props.filter.endValue && format(props.filter.endValue, dateFormat)}
				</Typography>
			);
		}

		return (
			<Typography color="primary" noWrap>
				{t(props.filter.rangeType)}
			</Typography>
		);
	}

	if (props.filter.selectionType === FilterSelectionType.CHIP) {
		return <></>;
	}

	if (props.filter.currencyCode) {
		return (
			<Typography color="primary" style={{ whiteSpace: "nowrap" }}>
				{renderCurrency(props.filter.startValue, props.filter.currencyCode)} -{" "}
				{renderCurrency(props.filter.endValue, props.filter.currencyCode)}
			</Typography>
		);
	}

	return (
		<Typography color="primary" style={{ whiteSpace: "nowrap" }}>
			{props.filter.startValue} - {props.filter.endValue}
		</Typography>
	);
};

const PermanentChip = ({ filter, dispatcher }: { filter: ChipFilterState; dispatcher: Dispatch<TableFilterAction> }) => {
	const { t } = useTranslation();
	const isEmpty = !filter.isActive;

	function handleClear(e: MouseEvent) {
		e.stopPropagation();
		e.preventDefault();
		dispatcher({
			type: "set-boolean-value",
			path: filter.field as string,
			value: false,
		});
	}

	function selectFilter() {
		dispatcher({
			type: "set-boolean-value",
			path: filter.field as string,
			value: true,
		});
	}

	const typographyStyles = isEmpty
		? {
				cursor: "pointer",
		  }
		: {
				color: "#367bf5",
		  };

	return (
		<Box pr={1}>
			<PermanentFilterButton variant="outlined" onClick={selectFilter} style={{ paddingLeft: isEmpty ? undefined : 0 }}>
				<Box maxWidth="100%" display="flex" alignItems={"center"} height={"26px"} pr={1}>
					{!isEmpty && (
						<>
							<ClearPermanentFilterButton onClick={(e) => handleClear(e as any)} style={{ flexShrink: 0 }}>
								<Close color="primary" style={{ fontSize: "1.1rem" }} />
							</ClearPermanentFilterButton>
							<Divider orientation="vertical" style={{ marginRight: "8px", backgroundColor: blueContrast }} />
						</>
					)}

					<Typography
						style={{
							...typographyStyles,
							paddingRight: "8px",
							whiteSpace: "nowrap",
						}}
					>
						{onlyFirstLetterUppercase(t(filter.localizationKey || (filter.field as string)))}
					</Typography>
					{isEmpty ? filter.icons?.iconOff && filter.icons?.iconOff() : filter.icons?.iconOn && filter.icons?.iconOn()}
				</Box>
			</PermanentFilterButton>
		</Box>
	);
};

const PermanentFilter: React.FC<IProps> = ({ filter, children, dispatcher, customOnSelectAction }) => {
	const [anchorEl, setAnchorEl] = useState(null);
	const { t } = useTranslation();
	const togglePopover = (event: any) => {
		setAnchorEl(anchorEl ? null : event.currentTarget);
	};

	const handlePopoverClose = () => setAnchorEl(null);

	function handleClear(e: MouseEvent) {
		e.stopPropagation();
		e.preventDefault();
		dispatcher({ type: "clear-selection", path: filter.field as string });
		customOnSelectAction && customOnSelectAction();
	}

	const isOpen = !!anchorEl;
	const isEmpty = isFilterEmpty(filter);

	const preventDefault = useCallback((e: any) => {
		e = e || window.event;
		if (e.preventDefault) {
			e.preventDefault();
		}
		e.returnValue = false;
	}, []);

	const enableScroll = () => {
		document.removeEventListener("wheel", preventDefault, false);
	};

	const disableScroll = () => {
		document.addEventListener("wheel", preventDefault, {
			passive: false,
		});
	};

	const getFilterListTooltip = (filter: ListFilterState) => {
		return (
			<>
				{filter.options
					.filter((option) => option.isSelected)
					.map((selectedOption) => (
						<Typography display="block">{selectedOption.value}</Typography>
					))}
			</>
		);
	};

	if (filter.selectionType === FilterSelectionType.CHIP) {
		return <PermanentChip filter={filter} dispatcher={dispatcher} />;
	}

	const enhancedChildren = React.cloneElement(children, {
		handlePopoverClose,
	});

	return (
		<>
			<Box pr={1} onMouseEnter={disableScroll} onMouseLeave={enableScroll}>
				<PermanentFilterButton
					variant="outlined"
					aria-haspopup="true"
					onClick={togglePopover}
					style={{ paddingLeft: isEmpty ? undefined : 0 }}
				>
					<Box maxWidth="100%" display="flex" alignItems={"center"} height={"26px"}>
						{!isEmpty && (
							<>
								<ClearPermanentFilterButton onClick={(e) => handleClear(e as any)} style={{ flexShrink: 0 }}>
									<Close color="primary" style={{ fontSize: "1.1rem" }} />
								</ClearPermanentFilterButton>
								<Divider orientation="vertical" style={{ marginRight: "8px", backgroundColor: blueContrast }} />
							</>
						)}
						{isEmpty ? (
							<Typography style={{ whiteSpace: "nowrap" }}>
								{onlyFirstLetterUppercase(t(filter.localizationKey || (filter.field as string)))}
							</Typography>
						) : (
							<LyfeTooltip
								placement="right-start"
								title={
									filter.selectionType === FilterSelectionType.LIST ? (
										getFilterListTooltip(filter)
									) : (
										<ActiveFilterContent filter={filter} isEllipsisRuleApplied={false} />
									)
								}
							>
								<Box display={"flex"} alignItems="center">
									<ActiveFilterContent filter={filter} isEllipsisRuleApplied={true} />
								</Box>
							</LyfeTooltip>
						)}
						<ExpandMore color="primary" />
					</Box>
				</PermanentFilterButton>
			</Box>
			<Popover
				open={isOpen}
				anchorEl={anchorEl}
				anchorOrigin={{
					vertical: "bottom",
					horizontal: "left",
				}}
				transformOrigin={{
					vertical: "top",
					horizontal: "left",
				}}
				onClose={handlePopoverClose}
				disableRestoreFocus
				disableScrollLock
			>
				{enhancedChildren}
			</Popover>
		</>
	);
};

export function PermanentFilters(props: {
	state: FilterListStateType;
	dispatcher: Dispatch<TableFilterAction>;
	customOnSelectAction?: () => void;
	hideScrollContainer?: boolean;
	groups?: FilterGroup[];
}) {
	const groupedFilterFields = new Set((props.groups || []).flatMap((group) => group.filters.map((filter) => String(filter.field))));
	const permanentFilters = props.state.filter((filter) => filter.isPermanent && !groupedFilterFields.has(String(filter.field)));
	const groupComponents = (props.groups || []).map((group) => {
		if (group.layout !== "tabs") {
			return null;
		}

		return (
			<TabsGroup
				key={`group-${group.id}`}
				tabs={group.filters.map((filter) => ({ filter }))}
				usePopover={true}
				dispatcher={props.dispatcher}
				exclusive={group.exclusive}
				customOnSelectAction={props.customOnSelectAction}
				onActiveTabChange={group.onActiveTabChange}
			/>
		);
	});
	const FiltersComponents = [
		...groupComponents.filter(Boolean),
		...permanentFilters.map((filter, index) => (
			<PermanentFilter key={index} filter={filter} dispatcher={props.dispatcher} customOnSelectAction={props.customOnSelectAction}>
				{getFilterComponent(filter, props.dispatcher, props.customOnSelectAction)}
			</PermanentFilter>
		)),
	];

	return props.hideScrollContainer ? (
		<>{FiltersComponents}</>
	) : (
		<HorizontalScrollContainer>{FiltersComponents}</HorizontalScrollContainer>
	);
}
